// ===== QR スキャン (搬入) =====

import { useMemo } from "react";
import { Alert, Stack, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { PageContext } from "../PageContext";
import { QRCodeInput } from "../../../components/input/QRCodeInput";
import useInput from "../../../util/useInput";
import { DetailGuard, useDetailFetch, useDetailRetry } from "../../../components/DetailFetch";
import { carryInRow } from "../../../schema/console/carry_in";
import { required } from "../../../util/typed";

export function CarryInScanPage({ pageContext }: { pageContext: PageContext }) {
  const api = pageContext.api;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [retryCount, retry] = useDetailRetry();
  const state = useDetailFetch<carryInRow>(() => {
    if (!id) {
      return Promise.resolve(null);
    }
    return api.carry_in.getFromId({ id });
  }, [api, id, retryCount]);
  const ci = state.status === "ready" ? state.data : null;

  const initialIds = useMemo(() => (ci ? ci.Materials.map((m) => m.id) : []), [ci]);
  const input = useInput<{ MaterialIDs: number[] }>({ MaterialIDs: initialIds }, () => {}, [ci]);

  const process = async (ids: number[]) => {
    try {
      await api.carry_in.process({ id: required(id, "URLの搬入ID"), MaterialIDs: ids });
      navigate(`/console/carry-in/${id}/`);
    } catch {
      api.showError("搬入の保存に失敗しました。再度お試しください。");
    }
  };

  if (state.status !== "ready") {
    return <DetailGuard state={state} notFoundLabel={`搬入 ${id}`} onRetry={retry}>{() => <></>}</DetailGuard>;
  }

  // 読み取り済み件数が予定数を超えたら警告する。サーバは超過を許容する(オフライン運用のため)ので
  // 保存自体は妨げず、超過したまま記録される旨だけ伝える。
  const scannedCount = input.getState("MaterialIDs").value?.length ?? 0;
  const isOverPlanned = state.data.require_material_count > 0 && scannedCount > state.data.require_material_count;

  return (
    <Stack width="100%" alignItems="center" pt="40px" gap={3}>
      <Typography variant="h5" fontWeight="bold">搬入QRコード読み取り</Typography>
      <Alert severity="info">予定 {state.data.require_material_count} 件の材料QRを読み取ってください。</Alert>
      {isOverPlanned && (
        <Alert severity="warning">搬入予定数を超過しています ({scannedCount}/{state.data.require_material_count})。このまま保存すると超過した搬入として記録されます。</Alert>
      )}
      <QRCodeInput state={input.getState("MaterialIDs")} onFinished={(value) => process(value ?? [])} onCancel={() => navigate(-1)} />
    </Stack>
  );
}
